import React, { useEffect } from 'react';
import {
  View,
  Text,
  Pressable,
  StyleSheet,
  StatusBar,
  Image,
  Dimensions,
  ScrollView,
  Linking,
  Platform,
} from 'react-native';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../services/AuthContext';
import CheckerPattern from '../components/CheckerPattern';
import Icon from 'react-native-vector-icons/MaterialIcons';

const { width: SCREEN_WIDTH } = Dimensions.get('window');
const isDesktop = SCREEN_WIDTH >= 1024;
const isTablet = SCREEN_WIDTH >= 768 && SCREEN_WIDTH < 1024;

// Feature data
const FEATURES = [
  {
    icon: 'map',
    title: 'Find Chess Nearby',
    description: 'Discover chess clubs, hotspots, and players in your area with our interactive map.',
  },
  {
    icon: 'people',
    title: 'Connect & Play',
    description: 'Message other players, join clubs, and find your next opponent over the board.',
  },
  {
    icon: 'emoji-events',
    title: 'Tournaments',
    description: 'Create, manage, and participate in local chess tournaments with ease.',
  },
  {
    icon: 'timer',
    title: 'Chess Clock',
    description: 'Built-in chess clock with customizable time controls for your OTB games.',
  },
];

// Social media links
const SOCIAL_LINKS = [
  { name: 'Instagram', url: 'https://www.instagram.com/chessmaps/', icon: '📷' },
  { name: 'LinkedIn', url: 'https://www.linkedin.com/company/chessmaps/about/', icon: 'in' },
  { name: 'Twitter', url: 'https://x.com/overtheboardapp', icon: '𝕏' },
  { name: 'TikTok', url: 'https://www.tiktok.com/@chessmapsapp', icon: '♪' },
];

const LandingPage = ({ navigation }) => {
  const { isDarkMode, toggleDarkMode } = useTheme();
  const { user } = useAuth();

  // Redirect authenticated users to the main app
  useEffect(() => {
    if (user) {
      navigation.replace('Main');
    }
  }, [user, navigation]);

  // Theme colors
  const theme = {
    bg: isDarkMode ? '#0A0A0A' : '#FAFAFA',
    card: isDarkMode ? '#141414' : '#FFFFFF',
    text: isDarkMode ? '#FFFFFF' : '#0A0A0A',
    textSecondary: isDarkMode ? '#888888' : '#666666',
    border: isDarkMode ? '#2A2A2A' : '#E5E5E5',
    inputBg: isDarkMode ? '#1A1A1A' : '#F5F5F5',
    accent: '#94EB98',
    accentDark: '#7BC97F',
  };

  const handleGetStarted = () => {
    navigation.navigate('Login');
  };

  return (
    <View style={[styles.container, { backgroundColor: theme.bg }, Platform.OS === 'web' && styles.containerWeb]}>
      <StatusBar barStyle={isDarkMode ? 'light-content' : 'dark-content'} />
      
      {/* Subtle checker pattern background */}
      <CheckerPattern isDarkMode={isDarkMode} opacity={0.006} />
      
      {/* Theme Toggle */}
      <Pressable 
        style={styles.themeToggle} 
        onPress={toggleDarkMode}
        accessibilityRole="button"
        accessibilityLabel="Toggle theme"
      >
        <Text style={[styles.themeToggleText, { color: theme.textSecondary }]}>
          {isDarkMode ? '☀️' : '🌙'}
        </Text>
      </Pressable>

      <ScrollView 
        style={[styles.scrollView, Platform.OS === 'web' && styles.scrollViewWeb]}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* Hero Section */}
        <View style={styles.heroSection}>
          <Image
            source={require('../../assets/chess-maps logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <View style={styles.brandNameContainer}>
            <Text style={[styles.brandName, { color: theme.text }]}>
              Chess<Text style={{ color: theme.accent }}>Maps</Text>
            </Text>
            <View style={[styles.betaBadge, { backgroundColor: theme.border }]}>
              <Text style={[styles.betaText, { color: theme.textSecondary }]}>BETA</Text>
            </View>
          </View>
          <Text style={[styles.heroTagline, { color: theme.textSecondary }]}>
            Find your next game, over the board.
          </Text>
          <Text style={[styles.heroDescription, { color: theme.textSecondary }]}>
            Connect with chess players near you, discover local clubs and hotspots, 
            and never miss a tournament in your area.
          </Text>

          {/* CTA Buttons */}
          <View style={styles.ctaContainer}>
            <Pressable
              style={({ pressed }) => [
                styles.primaryButton,
                { opacity: pressed ? 0.9 : 1 },
              ]}
              onPress={handleGetStarted}
              accessibilityRole="button"
              accessibilityLabel="Get Started"
            >
              <Text style={styles.primaryButtonText}>Get Started</Text>
              <Icon name="arrow-forward" size={20} color="#FFFFFF" style={{ marginLeft: 8 }} />
            </Pressable>
          </View>
        </View>

        {/* Features Section */}
        <View style={styles.featuresSection}>
          <Text style={[styles.sectionTitle, { color: theme.text }]}>
            Everything you need for OTB chess
          </Text>
          <View style={[styles.featuresGrid, isDesktop && styles.featuresGridDesktop]}>
            {FEATURES.map((feature, index) => (
              <View 
                key={index} 
                style={[
                  styles.featureCard, 
                  { 
                    backgroundColor: theme.card,
                    borderColor: theme.border,
                  },
                  isDesktop && styles.featureCardDesktop,
                ]}
              >
                <View style={[styles.featureIconContainer, { backgroundColor: `${theme.accent}20` }]}>
                  <Icon name={feature.icon} size={28} color={theme.accent} />
                </View>
                <Text style={[styles.featureTitle, { color: theme.text }]}>
                  {feature.title}
                </Text>
                <Text style={[styles.featureDescription, { color: theme.textSecondary }]}>
                  {feature.description}
                </Text>
              </View>
            ))}
          </View>
        </View>

        {/* Social Proof / Stats Section */}
        <View style={[styles.statsSection, { backgroundColor: theme.inputBg }]}>
          <View style={styles.statsGrid}>
            <View style={styles.statItem}>
              <Text style={[styles.statNumber, { color: theme.accent }]}>Tons of</Text>
              <Text style={[styles.statLabel, { color: theme.textSecondary }]}>Players</Text>
            </View>
            <View style={[styles.statDivider, { backgroundColor: theme.border }]} />
            <View style={styles.statItem}>
              <Text style={[styles.statNumber, { color: theme.accent }]}>Toronto</Text>
              <Text style={[styles.statLabel, { color: theme.textSecondary }]}>City</Text>
            </View>
            <View style={[styles.statDivider, { backgroundColor: theme.border }]} />
            <View style={styles.statItem}>
              <Text style={[styles.statNumber, { color: theme.accent }]}>200+</Text>
              <Text style={[styles.statLabel, { color: theme.textSecondary }]}>Clubs</Text>
            </View>
          </View>
          <Text style={[styles.comingSoonText, { color: theme.textSecondary }]}>
            Coming soon to Vancouver, NYC, and more cities!
          </Text>
        </View>

        {/* Call to Action Section */}
        <View style={styles.ctaSection}>
          <Text style={[styles.ctaSectionTitle, { color: theme.text }]}>
            Ready to find your next game?
          </Text>
          <Text style={[styles.ctaSectionDescription, { color: theme.textSecondary }]}>
            Join the growing community of chess players connecting over the board.
          </Text>
          <Pressable
            style={({ pressed }) => [
              styles.primaryButton,
              styles.ctaSectionButton,
              { opacity: pressed ? 0.9 : 1 },
            ]}
            onPress={handleGetStarted}
            accessibilityRole="button"
            accessibilityLabel="Sign Up Now"
          >
            <Text style={styles.primaryButtonText}>Sign Up Now</Text>
          </Pressable>
        </View>

        {/* Footer */}
        <View style={[styles.footer, { borderTopColor: theme.border }]}>
          <View style={styles.footerContent}>
            <Text style={[styles.footerBrand, { color: theme.text }]}>
              Chess<Text style={{ color: theme.accent }}>Maps</Text>
            </Text>
            
            {/* Social Links */}
            <View style={styles.socialLinksContainer}>
              {SOCIAL_LINKS.map((social) => (
                <Pressable
                  key={social.name}
                  style={({ pressed }) => [
                    styles.socialLinkButton,
                    { 
                      backgroundColor: theme.card,
                      borderColor: theme.border,
                      opacity: pressed ? 0.7 : 1,
                    },
                  ]}
                  onPress={() => Linking.openURL(social.url)}
                  accessibilityRole="link"
                  accessibilityLabel={`Follow us on ${social.name}`}
                >
                  <Text style={[styles.socialIcon, { color: theme.text }]}>
                    {social.icon}
                  </Text>
                </Pressable>
              ))}
            </View>

            <Text style={[styles.footerCopyright, { color: theme.textSecondary }]}>
              © 2024 ChessMaps. All rights reserved.
            </Text>
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  // Web: give landing a fixed viewport height so ScrollView inside can scroll (global mobile CSS sets overflow:hidden on body/#root)
  containerWeb: Platform.OS === 'web' ? {
    height: '100vh',
    maxHeight: '100dvh',
    overflow: 'hidden',
  } : {},
  scrollView: {
    flex: 1,
  },
  // Web: ensure the scroll container can shrink and scroll when parent has fixed height
  scrollViewWeb: Platform.OS === 'web' ? {
    minHeight: 0,
    overflow: 'scroll',
    WebkitOverflowScrolling: 'touch',
  } : {},
  themeToggle: {
    position: 'absolute',
    top: 50,
    right: 20,
    zIndex: 10,
    padding: 10,
  },
  themeToggleText: {
    fontSize: 24,
  },
  scrollContent: {
    flexGrow: 1,
    paddingTop: 80,
    paddingBottom: Platform.OS === 'web' ? 24 : 40,
  },
  // Hero Section
  heroSection: {
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 48,
    maxWidth: 600,
    alignSelf: 'center',
    width: '100%',
  },
  logo: {
    width: 120,
    height: 120,
    marginBottom: 20,
  },
  brandNameContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  brandName: {
    fontSize: 42,
    fontWeight: '700',
    letterSpacing: -1,
  },
  betaBadge: {
    paddingVertical: 3,
    paddingHorizontal: 8,
    borderRadius: 4,
  },
  betaText: {
    fontSize: 10,
    fontWeight: '600',
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  heroTagline: {
    fontSize: 20,
    fontWeight: '500',
    textAlign: 'center',
    marginBottom: 16,
  },
  heroDescription: {
    fontSize: 16,
    lineHeight: 26,
    textAlign: 'center',
    marginBottom: 32,
    maxWidth: 480,
  },
  ctaContainer: {
    flexDirection: 'row',
    gap: 16,
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  primaryButton: {
    backgroundColor: '#84D288',
    paddingVertical: 16,
    paddingHorizontal: 32,
    borderRadius: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    // Shadow
    elevation: 8,
    shadowColor: '#76AF87',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 0,
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: 'bold',
  },
  secondaryButton: {
    paddingVertical: 16,
    paddingHorizontal: 32,
    borderRadius: 12,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  secondaryButtonText: {
    fontSize: 18,
    fontWeight: '600',
  },
  // Features Section
  featuresSection: {
    paddingHorizontal: 24,
    paddingVertical: 48,
    alignItems: 'center',
  },
  sectionTitle: {
    fontSize: 28,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 32,
    letterSpacing: -0.5,
  },
  featuresGrid: {
    width: '100%',
    maxWidth: 1200,
    gap: 20,
  },
  featuresGridDesktop: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  featureCard: {
    padding: 24,
    borderRadius: 16,
    borderWidth: 1,
    marginBottom: 16,
    ...(Platform.OS === 'web' ? {
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    } : {}),
  },
  featureCardDesktop: {
    width: '45%',
    maxWidth: 280,
    margin: 10,
  },
  featureIconContainer: {
    width: 56,
    height: 56,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  featureTitle: {
    fontSize: 20,
    fontWeight: '600',
    marginBottom: 8,
  },
  featureDescription: {
    fontSize: 15,
    lineHeight: 24,
  },
  // Stats Section
  statsSection: {
    paddingVertical: 40,
    paddingHorizontal: 24,
    marginVertical: 24,
  },
  statsGrid: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    maxWidth: 600,
    alignSelf: 'center',
    width: '100%',
  },
  statItem: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 8,
  },
  statNumber: {
    fontSize: 36,
    fontWeight: '700',
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 14,
    fontWeight: '500',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  statDivider: {
    width: 1,
    height: 50,
    marginHorizontal: 24,
  },
  comingSoonText: {
    fontSize: 14,
    textAlign: 'center',
    marginTop: 20,
    fontStyle: 'italic',
  },
  // CTA Section
  ctaSection: {
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 48,
  },
  ctaSectionTitle: {
    fontSize: 28,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 12,
    letterSpacing: -0.5,
  },
  ctaSectionDescription: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 24,
    maxWidth: 400,
  },
  ctaSectionButton: {
    marginTop: 8,
  },
  // Footer
  footer: {
    borderTopWidth: 1,
    paddingVertical: 32,
    paddingHorizontal: 24,
    marginTop: 24,
  },
  footerContent: {
    alignItems: 'center',
    gap: 20,
  },
  footerBrand: {
    fontSize: 24,
    fontWeight: '700',
    letterSpacing: -0.5,
  },
  socialLinksContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 16,
  },
  socialLinkButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
  },
  socialIcon: {
    fontSize: 18,
    fontWeight: '700',
  },
  footerCopyright: {
    fontSize: 13,
  },
});

export default LandingPage;

